import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { ViewToggle, ViewMode } from "@/components/common/ViewToggle";
import { Slider } from "@/components/ui/slider";
import { useTranslation } from "react-i18next";
import { ReactNode } from "react";

export type SortField = "name" | "dateCreated" | "matchScore";
export type SortOrder = "asc" | "desc";
export type { ViewMode }; // Changed to export type instead of just export

interface CandidatesViewOptionsProps {
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  allowedModes?: ViewMode[];
  extra?: ReactNode;
  cardMinWidth?: number;
  setCardMinWidth?: (size: number) => void;
  minCardWidth?: number;
  maxCardWidth?: number;
  cardWidthStep?: number;
}

export const CandidatesViewOptions = ({
  sortField,
  setSortField,
  sortOrder,
  setSortOrder,
  viewMode,
  setViewMode,
  allowedModes = ["card", "list"],
  extra,
  cardMinWidth,
  setCardMinWidth,
  minCardWidth = 220,
  maxCardWidth = 480,
  cardWidthStep = 20,
}: CandidatesViewOptionsProps) => {
  const { t } = useTranslation();
  const getSortLabel = () => {
    switch (sortField) {
      case "name": return t("sortBar.name");
      case "dateCreated": return t("sortBar.dateCreated");
      case "matchScore": return t("sortBar.matchScore");
      default: return t("sortBar.sortBy");
    }
  };

  const handleToggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const showSizeSlider = cardMinWidth !== undefined && setCardMinWidth && viewMode === "card";

  return (
    <div className="flex justify-between items-center mb-4 bg-white dark:bg-gray-800 p-3 rounded-sm shadow-sm">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{t("sortBar.sortBy")}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              {getSortLabel()}
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => setSortField("name")}>{t("sortBar.name")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortField("dateCreated")}>{t("sortBar.dateCreated")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSortField("matchScore")}>{t("sortBar.matchScore")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleToggleSortOrder}
          className="ml-1"
        >
          {sortOrder === "asc" ? t("sortBar.ascending") : t("sortBar.descending")}
        </Button>
        {extra}
      </div>
      <div className="flex items-center gap-4">
        {showSizeSlider && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {t("sortBar.cardSize")}
            </span>
            <Slider
              className="w-32"
              min={minCardWidth}
              max={maxCardWidth}
              step={cardWidthStep}
              value={[cardMinWidth as number]}
              onValueChange={(values) => setCardMinWidth?.(values[0])}
              aria-label={t("sortBar.cardSize")}
            />
          </div>
        )}
        <ViewToggle
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          label={t("sortBar.view")}
          allowedModes={allowedModes}
        />
      </div>
    </div>
  );
};
